import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import type { GiftProduct, GiftProductStockAdjustPayload } from '@/api/gift-products'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface GiftProductStockAdjustDialogProps {
	open: boolean
	product: GiftProduct | null
	isSaving: boolean
	onOpenChange: (open: boolean) => void
	onSubmit: (payload: GiftProductStockAdjustPayload) => void
}

export function GiftProductStockAdjustDialog({
	open,
	product,
	isSaving,
	onOpenChange,
	onSubmit,
}: GiftProductStockAdjustDialogProps) {
	const [delta, setDelta] = useState('')
	const [remark, setRemark] = useState('')

	useEffect(() => {
		if (!open) return
		setDelta('')
		setRemark('')
	}, [open, product])

	const handleSubmit = () => {
		const parsedDelta = Number.parseInt(delta, 10)
		if (!Number.isFinite(parsedDelta) || parsedDelta === 0) {
			toast.error('调整数量不能为 0')
			return
		}
		onSubmit({
			delta: parsedDelta,
			remark: remark.trim(),
		})
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>调整库存</DialogTitle>
					<DialogDescription>{product ? `${product.name} 当前库存 ${product.stock}` : '调整礼物库存'}</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-3 text-sm">
						<div className="rounded-md border px-3 py-2">
							<div className="text-muted-foreground">当前库存</div>
							<div className="mt-1 text-lg font-semibold">{product?.stock ?? '-'}</div>
						</div>
						<div className="rounded-md border px-3 py-2">
							<div className="text-muted-foreground">调整后</div>
							<div className="mt-1 text-lg font-semibold">{previewStock(product?.stock, delta)}</div>
						</div>
					</div>
					<div className="space-y-2">
						<Label htmlFor="gift-product-stock-delta">调整数量</Label>
						<Input
							id="gift-product-stock-delta"
							type="number"
							value={delta}
							onChange={(event) => setDelta(event.target.value)}
							placeholder="正数入库，负数扣减"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="gift-product-stock-remark">备注</Label>
						<Textarea
							id="gift-product-stock-remark"
							value={remark}
							maxLength={255}
							onChange={(event) => setRemark(event.target.value)}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
						取消
					</Button>
					<Button type="button" onClick={handleSubmit} disabled={isSaving || !product}>
						确认调整
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function previewStock(stock: number | undefined, delta: string) {
	if (stock === undefined) return '-'
	const parsedDelta = Number.parseInt(delta, 10)
	if (!Number.isFinite(parsedDelta)) return '-'
	return stock + parsedDelta
}
